// Unified API facade with backward-compatible named exports
//
// REACT_APP_BACKEND_URL is inlined by CRA at build time:
//   - Local dev:        not set → "" → relative /api/* URLs → CRA dev proxy → local backend :5050
//   - Cloudflare Pages: Pages build env sets it to https://api.adlensfx.com → bundle
//                       calls that host directly (see apiBase.js for the full note)
//
import { authHeader, handleAuthResponse, notifyAuthExpired } from "./authFetch";

const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export const BASE    = `${BACKEND}/api/har`;

export function getSessionId() {
  let id = sessionStorage.getItem('validator_session_id');
  if (!id) {
    id = Math.random().toString(36).substring(2) + Date.now().toString(36);
    sessionStorage.setItem('validator_session_id', id);
  }
  return id;
}

/** Low-level helper. Attaches the bearer token, consumes any rotated token from
 *  the X-Refreshed-Token header (sliding session), and on a 401 triggers the
 *  shared graceful re-auth flow instead of throwing an opaque error. */
export async function fetchJSON(url, opts = {}) {
  const headers = { "Content-Type": "application/json", ...authHeader(), ...(opts.headers || {}) };
  const body = opts.body && (typeof opts.body === "string" ? opts.body : JSON.stringify(opts.body));
  const resp = await fetch(url, { ...opts, headers, body });
  handleAuthResponse(resp);
  if (!resp.ok) {
    if (resp.status === 401) notifyAuthExpired();
    const text = await resp.text().catch(() => "");
    throw new Error(`HTTP ${resp.status}: ${text}`);
  }
  const ct = resp.headers.get("content-type") || "";
  if (!/application\/json/i.test(ct)) return resp;
  return await resp.json();
}

/** Modern API object */
export const api = {
  save: (payload) => fetchJSON(`${BASE}`, { method: "POST", body: payload }),
  // Accepts either a bare cursor string (legacy call shape, unchanged
  // behavior) or a params object: { cursor?, q?, favorite?, limit?, offset?, sort? }
  // — mirrors GET /api/har's query params (backend/index.js:352-365).
  list: (paramsOrCursor) => {
    const qs = new URLSearchParams();
    if (typeof paramsOrCursor === "string") {
      if (paramsOrCursor) qs.set("cursor", paramsOrCursor);
    } else if (paramsOrCursor && typeof paramsOrCursor === "object") {
      const { cursor, q, favorite, limit, offset, sort } = paramsOrCursor;
      if (cursor) qs.set("cursor", cursor);
      if (q) qs.set("q", q);
      if (favorite != null) qs.set("favorite", favorite ? "1" : "0");
      if (limit != null) qs.set("limit", String(limit));
      if (offset != null) qs.set("offset", String(offset));
      if (sort) qs.set("sort", sort);
    }
    const s = qs.toString();
    return fetchJSON(`${BASE}${s ? `?${s}` : ""}`);
  },
  get: (id) => fetchJSON(`${BASE}/${encodeURIComponent(id)}`),
  del: (id) => fetchJSON(`${BASE}/${encodeURIComponent(id)}`, { method: "DELETE" }),
  fav: (id, favorite) =>
    fetchJSON(`${BASE}/${encodeURIComponent(id)}/favorite`, {
      method: "POST",
      body: { favorite: !!favorite },
    }),
  download: (id, { format = "har", asAttachment = true } = {}) =>
    fetch(`${BASE}/${encodeURIComponent(id)}?download=${asAttachment ? "1" : "0"}&format=${encodeURIComponent(format)}`, { headers: { ...authHeader() } }),
};

/* ------------------------------------------------------------------
 * Backward-compatible named exports
 * ------------------------------------------------------------------
 */
export async function saveHarToServer(har, { title = "", favorite = false, labels = [], pageUrl = "" } = {}) {
  return api.save({ har, title, favorite, labels, pageUrl });
}
export function listSavedHars({ cursor } = {}) { return api.list(cursor); }
export function setFavorite(id, favorite) { return api.fav(id, favorite); }
export function deleteHar(id) { return api.del(id); }
export async function downloadHar(id, { format = "har" } = {}) {
  const resp = await api.download(id, { format, asAttachment: true });
  handleAuthResponse(resp);
  if (!resp.ok) {
    if (resp.status === 401) notifyAuthExpired();
    throw new Error(`Download failed: HTTP ${resp.status}`);
  }
  const blob = await resp.blob();
  const cd = resp.headers.get("Content-Disposition") || "";
  const match = /filename="?([^"]+)"?/i.exec(cd);
  const filename = match ? match[1] : `capture.${format}`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}
